import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
  Shield, CheckCircle, Calendar, Users, Info, Plus, Search, 
  UserPlus, Mail, Phone, Briefcase, Hash, Lock, CheckCircle2, AlertCircle, Key, ChevronDown, ChevronUp, Trash2, Pencil
} from 'lucide-react';
import UIElementGuard from './UIElementGuard';
import ConfirmModal from './ConfirmModal';
import './CustomRoleManagementPage.css';

function CustomRoleManagementPage({ role, onRefreshRoles, isDeactivatedView = false }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    employee_id: '',
    department: '',
    phone: '',
    password: '',
    status: 'Active'
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    employee_id: '',
    department: '',
    phone: '',
    password: '',
    status: 'Active'
  });
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editErrorMessage, setEditErrorMessage] = useState('');

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: null
  });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    if (role && role.id) {
      fetchUsers();
    }
  }, [role, isDeactivatedView]);

  const handleToggleStatus = async (userItem) => {
    const isCurrentlyActive = userItem.status?.toLowerCase() === 'active' || userItem.is_active;
    const newActive = !isCurrentlyActive;
    const newStatus = newActive ? 'Active' : 'Deactivated';
    const actionText = newActive ? 'activate' : 'deactivate';
    
    setConfirmModal({
      isOpen: true,
      title: `Confirm ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      message: `Are you sure you want to ${actionText} ${userItem.name || 'this user'}?`,
      type: newActive ? 'primary' : 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.put(`/university/users/${userItem.id}/status`, {
            is_active: newActive,
            status: newStatus
          });
          fetchUsers();
        } catch (err) {
          console.error("Failed to update user status", err);
          setErrorMessage(err.response?.data?.detail || `Failed to ${actionText} user`);
        }
      }
    });
  };

  const fetchUsers = async () => {
    if (!role || !role.id) return;
    try {
      setLoadingUsers(true);
      setErrorMessage('');
      const res = await api.get(`/university/roles/${role.id}/users?status=${isDeactivatedView ? 'deactivated' : 'active'}`);
      setUsers(res.data || []);
    } catch (error) {
      console.error("Failed to fetch custom role users", error);
      setErrorMessage(error.response?.data?.detail || "Failed to load assigned users. Please refresh the page.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!formData.name || !formData.name.trim()) {
      errors.name = "Full Name is required.";
    }

    if (!formData.email || !formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailPattern.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address (e.g. name@university.edu).";
    }

    if (!formData.employee_id || !formData.employee_id.trim()) {
      errors.employee_id = "Employee ID is required.";
    }

    if (!formData.department || !formData.department.trim()) {
      errors.department = "Department is required.";
    }

    if (!formData.phone || !formData.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else {
      const cleanPhone = formData.phone.trim();
      if (!/^\d{10}$/.test(cleanPhone)) {
        errors.phone = "Phone number must contain exactly 10 digits.";
      }
    }

    if (formData.password && formData.password.trim() && formData.password.trim().length < 6) {
      errors.password = "Password must be at least 6 characters long.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddModal = () => {
    setErrorMessage('');
    setFieldErrors({});
    setFormData({
      name: '',
      email: '',
      employee_id: '',
      department: '',
      phone: '',
      password: '',
      status: 'Active'
    });
    setShowAddModal(true);
  };

  const formatErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
      return err.map(item => formatErrorMessage(item)).filter(Boolean).join('; ');
    }
    if (typeof err === 'object') {
      if (err.msg) return err.msg;
      if (err.detail) return formatErrorMessage(err.detail);
      if (err.message) return formatErrorMessage(err.message);
      try {
        return JSON.stringify(err);
      } catch (e) {
        return 'An error occurred.';
      }
    }
    return String(err);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setErrorMessage("Please fix the validation errors highlighted below.");
      return;
    }
    try {
      setIsSaving(true);
      setErrorMessage('');
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        employee_id: formData.employee_id.trim() || null,
        department: formData.department.trim() || null,
        phone: formData.phone.trim() || null,
        password: formData.password.trim() || null,
        status: formData.status
      };

      await api.post(`/university/roles/${role.id}/users`, payload);
      showToast(`${roleName} user created successfully!`);
      setShowAddModal(false);
      fetchUsers();
      if (typeof onRefreshRoles === 'function') {
        onRefreshRoles();
      }
    } catch (error) {
      console.error("Failed to create role user", error);
      const msg = formatErrorMessage(error.response?.data?.detail) || "An error occurred while creating the user.";
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setEditErrorMessage('');
    setEditFieldErrors({});
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      employee_id: user.employee_id || '',
      department: user.department || '',
      phone: user.phone || user.phone_number || '',
      password: '',
      status: user.status || 'Active'
    });
    setShowEditModal(true);
  };

  const validateEditForm = () => {
    const errors = {};
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!editFormData.name || !editFormData.name.trim()) {
      errors.name = "Full Name is required.";
    }

    if (!editFormData.email || !editFormData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailPattern.test(editFormData.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }

    if (!editFormData.employee_id || !editFormData.employee_id.trim()) {
      errors.employee_id = "Employee ID is required.";
    }

    if (!editFormData.department || !editFormData.department.trim()) {
      errors.department = "Department is required.";
    }

    if (!editFormData.phone || !editFormData.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else {
      const cleanPhone = editFormData.phone.trim();
      if (!/^\d{10}$/.test(cleanPhone)) {
        errors.phone = "Phone number must contain exactly 10 digits.";
      }
    }

    if (editFormData.password && editFormData.password.trim() && editFormData.password.trim().length < 6) {
      errors.password = "Password must be at least 6 characters long.";
    }

    setEditFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) {
      setEditErrorMessage("Please fix the validation errors highlighted below.");
      return;
    }
    try {
      setIsSaving(true);
      setEditErrorMessage('');
      const payload = {
        name: editFormData.name.trim(),
        email: editFormData.email.trim(),
        employee_id: editFormData.employee_id.trim() || null,
        department: editFormData.department.trim() || null,
        phone: editFormData.phone.trim() || null,
        password: editFormData.password.trim() || null,
        status: editFormData.status
      };

      await api.put(`/university/roles/${role.id}/users/${editingUser.id}`, payload);
      showToast(`${roleName} user updated successfully!`);
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Failed to update role user", error);
      const msg = formatErrorMessage(error.response?.data?.detail) || "An error occurred while updating the user.";
      setEditErrorMessage(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setConfirmModal({
      isOpen: true,
      title: `Delete ${roleName}`,
      message: `Are you sure you want to delete this ${roleName}?`,
      type: 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.delete(`/university/roles/${role.id}/users/${userId}`);
          showToast(`${roleName} deleted successfully!`);
          fetchUsers();
          if (typeof onRefreshRoles === 'function') {
            onRefreshRoles();
          }
        } catch (error) {
          console.error("Failed to delete role user", error);
          alert(formatErrorMessage(error.response?.data?.detail) || "An error occurred while deleting the user.");
        }
      }
    });
  };

  const showToast = (msg) => {
    setSuccessMessage(formatErrorMessage(msg));
    setTimeout(() => {
      setSuccessMessage('');
    }, 4000);
  };

  if (!role) {
    return (
      <div className="custom-role-page-loading">
        <div className="spinner"></div>
        <p>Loading Custom Role details...</p>
      </div>
    );
  }

  const roleName = role.name || role.role_name || 'Custom Role';
  const permissions = role.permissions || [];
  const permCount = role.permission_count !== undefined ? role.permission_count : permissions.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const filteredUsers = users.filter(u => {
    const term = userSearchTerm.toLowerCase();
    const matchName = (u.name || '').toLowerCase().includes(term);
    const matchEmail = (u.email || '').toLowerCase().includes(term);
    const matchEmpId = (u.employee_id || '').toLowerCase().includes(term);
    const matchDept = (u.department || '').toLowerCase().includes(term);
    return matchName || matchEmail || matchEmpId || matchDept;
  });

  return (
    <div className="custom-role-page-container pop-fade-up">
      {successMessage && (
        <div className="alert-toast success-toast">
          <CheckCircle2 size={18} />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && !showAddModal && (
        <div className="alert-toast error-toast">
          <AlertCircle size={18} />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Top Banner Card */}
      <div className="custom-role-banner">
        <div className="banner-left">
          <div className="role-badge-icon">
            <Shield size={32} />
          </div>
          <div>
            <div className="banner-title-row">
              <h2>{roleName}</h2>
              <span className="role-id-badge">Role ID: #{role.id}</span>
              <span className="custom-role-tag">Generic User Management</span>
            </div>
            <p className="banner-description">
              {role.description ? role.description : `Manage and create users assigned to the ${roleName} role.`}
            </p>
          </div>
        </div>

        <div className="banner-right">
          <button 
            type="button" 
            className="toggle-permissions-btn"
            onClick={() => setShowPermissions(!showPermissions)}
          >
            <Key size={15} />
            <span>{permCount} {permCount === 1 ? 'Permission Assigned' : 'Permissions Assigned'}</span>
            {showPermissions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <div className="meta-info-pill">
            <Calendar size={14} />
            <span>Created: {formatDate(role.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Collapsible Granular Permissions Preview */}
      {showPermissions && (
        <div className="role-permissions-accordion pop-fade-up">
          <div className="accordion-header">
            <h4>Assigned System Privileges for {roleName}</h4>
            <p className="text-muted">All users listed below automatically inherit these privileges across the platform.</p>
          </div>
          <div className="perm-chips-grid">
            {permissions.length === 0 ? (
              <span className="text-muted italic">No specific system permissions currently selected for this role.</span>
            ) : (
              permissions.map(p => (
                <div key={p.id} className="perm-preview-chip">
                  <CheckCircle size={14} className="perm-chip-icon" />
                  <div>
                    <strong className="perm-chip-name">{p.name.replace(/_/g, ' ')}</strong>
                    {p.description && <p className="perm-chip-desc">{p.description}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main User Management Section */}
      <div className="user-management-card">
        <div className="management-toolbar">
          <div className="toolbar-search">
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder={`Search ${roleName}...`} 
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
            />
            {userSearchTerm && (
              <button className="clear-search-btn" onClick={() => setUserSearchTerm('')}>×</button>
            )}
          </div>

          <div className="toolbar-actions">
            <div className="users-count-pill">
              <span>Total {roleName}s: <strong>{filteredUsers.length}</strong></span>
            </div>
            <UIElementGuard page="custom_role_mgmt" element="add_user_to_role_btn">
              <button className="primary-btn pulse-glow" onClick={handleOpenAddModal}>
                <Plus size={18} /> Add {roleName}
              </button>
            </UIElementGuard>
          </div>
        </div>

        {/* Table / Empty State Content */}
        {loadingUsers ? (
          <div className="users-table-loading">
            <div className="spinner"></div>
            <p>Loading {roleName} users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="role-empty-state">
            <div className="empty-state-icon-wrapper">
              <UserPlus size={44} />
            </div>
            <h3>No {roleName}s Found</h3>
            <p>
              {userSearchTerm 
                ? `No users matching "${userSearchTerm}" were found under the ${roleName} role.`
                : `You haven't created any users assigned to the ${roleName} role yet. Create your first ${roleName} user below.`}
            </p>
            {!userSearchTerm && (
              <UIElementGuard page="custom_role_mgmt" element="add_user_to_role_btn">
                <button className="primary-btn mt-4" onClick={handleOpenAddModal}>
                  <Plus size={16} /> Add {roleName}
                </button>
              </UIElementGuard>
            )}
          </div>
        ) : (
          <div className="role-users-table-container">
            <table className="role-users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Employee ID</th>
                  <th>Department</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} className="user-table-row">
                    <td>
                      <div className="user-name-cell">
                        <div className="user-avatar-circle">
                          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <span className="user-name-text">{user.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="user-info-cell">
                        <Mail size={14} className="text-muted" />
                        <span>{user.email}</span>
                      </div>
                    </td>
                    <td>
                      <span className="emp-id-badge">
                        <Hash size={13} /> {user.employee_id || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="user-info-cell">
                        <Briefcase size={14} className="text-muted" />
                        <span>{user.department || 'General'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="user-info-cell">
                        <Phone size={14} className="text-muted" />
                        <span>{user.phone || 'N/A'}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${user.status?.toLowerCase() === 'active' ? 'status-active' : 'status-inactive'}`}>
                        {user.status || 'Active'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {!isDeactivatedView && (
                          <UIElementGuard page="custom_role_mgmt" element="edit_user_role_btn">
                            <button
                              onClick={() => handleToggleStatus(user)}
                              style={{
                                background: user.status?.toLowerCase() === 'active' ? '#fff1f2' : '#f0fdf4',
                                color: user.status?.toLowerCase() === 'active' ? '#e11d48' : '#16a34a',
                                border: `1px solid ${user.status?.toLowerCase() === 'active' ? '#fda4af' : '#86efac'}`,
                                cursor: 'pointer',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={user.status?.toLowerCase() === 'active' ? "Deactivate Account" : "Activate Account"}
                            >
                              {user.status?.toLowerCase() === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                          </UIElementGuard>
                        )}
                        {isDeactivatedView && (
                          <UIElementGuard page="custom_role_mgmt" element="edit_user_role_btn">
                            <button
                              onClick={() => handleToggleStatus(user)}
                              style={{
                                background: '#f0fdf4',
                                color: '#16a34a',
                                border: '1px solid #86efac',
                                cursor: 'pointer',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title="Activate Account"
                            >
                              Activate
                            </button>
                          </UIElementGuard>
                        )}
                        {!isDeactivatedView && (
                          <UIElementGuard page="custom_role_mgmt" element="edit_user_role_btn">
                            <button 
                              className="icon-btn edit-btn" 
                              onClick={() => handleOpenEditModal(user)} 
                              title="Edit User"
                              style={{ color: '#3b82f6', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                            >
                              <Pencil size={16} />
                            </button>
                          </UIElementGuard>
                        )}
                        <UIElementGuard page="custom_role_mgmt" element="delete_user_role_btn">
                          <button
                            className="icon-btn delete-btn"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete User"
                            style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </UIElementGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content user-modal pop-fade-up">
            <div className="modal-header">
              <div className="modal-title-wrapper">
                <UserPlus size={22} className="accent-icon" />
                <h3>Add {roleName}</h3>
              </div>
              <button className="close-modal-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>

            {errorMessage && (
              <div className="modal-error-banner">
                <AlertCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleCreateUser}>
              <div className="modal-form-grid">
                <div className="form-group">
                  <label htmlFor="user-name">Name <span className="req-star">*</span></label>
                  <input 
                    id="user-name"
                    type="text" 
                    value={formData.name} 
                    onChange={(e) => {
                      setFormData({...formData, name: e.target.value});
                      if (fieldErrors.name) setFieldErrors({...fieldErrors, name: null});
                    }}
                    placeholder="e.g. Dr. Rajesh Kumar"
                    style={{ borderColor: fieldErrors.name ? '#ef4444' : undefined }}
                    required
                  />
                  {fieldErrors.name && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="user-email">Email <span className="req-star">*</span></label>
                  <input 
                    id="user-email"
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => {
                      setFormData({...formData, email: e.target.value});
                      if (fieldErrors.email) setFieldErrors({...fieldErrors, email: null});
                    }}
                    placeholder={`e.g. hod.cs@university.edu`}
                    style={{ borderColor: fieldErrors.email ? '#ef4444' : undefined }}
                    required
                  />
                  {fieldErrors.email && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="user-emp-id">Employee ID <span className="req-star">*</span></label>
                  <input 
                    id="user-emp-id"
                    type="text" 
                    value={formData.employee_id} 
                    onChange={(e) => {
                      setFormData({...formData, employee_id: e.target.value});
                      if (fieldErrors.employee_id) setFieldErrors({...fieldErrors, employee_id: null});
                    }}
                    placeholder="e.g. EMP-2026-088"
                    style={{ borderColor: fieldErrors.employee_id ? '#ef4444' : undefined }}
                    required
                  />
                  {fieldErrors.employee_id && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.employee_id}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="user-dept">Department <span className="req-star">*</span></label>
                  <input 
                    id="user-dept"
                    type="text" 
                    value={formData.department} 
                    onChange={(e) => {
                      setFormData({...formData, department: e.target.value});
                      if (fieldErrors.department) setFieldErrors({...fieldErrors, department: null});
                    }}
                    placeholder="e.g. Computer Science & Engineering"
                    style={{ borderColor: fieldErrors.department ? '#ef4444' : undefined }}
                    required
                  />
                  {fieldErrors.department && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.department}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="user-phone">Phone Number <span className="req-star">*</span></label>
                  <input 
                    id="user-phone"
                    type="text" 
                    value={formData.phone} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({...formData, phone: val});
                      if (fieldErrors.phone) setFieldErrors({...fieldErrors, phone: null});
                    }}
                    maxLength={10}
                    placeholder="e.g. 9876543210"
                    style={{ borderColor: fieldErrors.phone ? '#ef4444' : undefined }}
                    required
                  />
                  {fieldErrors.phone && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="user-status">Status</label>
                  <select 
                    id="user-status"
                    className="modal-select"
                    value={formData.status} 
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="user-pwd">Initial Password (Optional)</label>
                  <div className="password-input-wrapper">
                    <Lock size={16} className="pwd-icon" />
                    <input 
                      id="user-pwd"
                      type="password" 
                      value={formData.password} 
                      onChange={(e) => {
                        setFormData({...formData, password: e.target.value});
                        if (fieldErrors.password) setFieldErrors({...fieldErrors, password: null});
                      }}
                      placeholder="Leave blank to auto-generate secure temporary password"
                      style={{ borderColor: fieldErrors.password ? '#ef4444' : undefined }}
                    />
                  </div>
                  {fieldErrors.password && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.password}</span>}
                </div>
              </div>


              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddModal(false)} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={isSaving}>
                  {isSaving ? (
                    <span className="btn-saving-text"><span className="spinner-small"></span> Creating {roleName}...</span>
                  ) : (
                    `+ Add ${roleName}`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal-content user-modal pop-fade-up">
            <div className="modal-header">
              <div className="modal-title-wrapper">
                <Pencil size={22} className="accent-icon" />
                <h3>Edit {roleName}</h3>
              </div>
              <button className="close-modal-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>

            {editErrorMessage && (
              <div className="modal-error-banner">
                <AlertCircle size={16} />
                <span>{editErrorMessage}</span>
              </div>
            )}

            <form onSubmit={handleUpdateUser}>
              <div className="modal-form-grid">
                <div className="form-group">
                  <label htmlFor="edit-user-name">Name <span className="req-star">*</span></label>
                  <input 
                    id="edit-user-name"
                    type="text" 
                    value={editFormData.name} 
                    onChange={(e) => {
                      setEditFormData({...editFormData, name: e.target.value});
                      if (editFieldErrors.name) setEditFieldErrors({...editFieldErrors, name: null});
                    }}
                    placeholder="e.g. Dr. Ramesh Kumar"
                    style={{ borderColor: editFieldErrors.name ? '#ef4444' : undefined }}
                    required
                  />
                  {editFieldErrors.name && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editFieldErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-user-email">Email <span className="req-star">*</span></label>
                  <input 
                    id="edit-user-email"
                    type="email" 
                    value={editFormData.email} 
                    onChange={(e) => {
                      setEditFormData({...editFormData, email: e.target.value});
                      if (editFieldErrors.email) setEditFieldErrors({...editFieldErrors, email: null});
                    }}
                    placeholder="e.g. hod.cs@university.edu"
                    style={{ borderColor: editFieldErrors.email ? '#ef4444' : undefined }}
                    required
                  />
                  {editFieldErrors.email && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editFieldErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-user-empid">Employee ID <span className="req-star">*</span></label>
                  <input 
                    id="edit-user-empid"
                    type="text" 
                    value={editFormData.employee_id} 
                    onChange={(e) => {
                      setEditFormData({...editFormData, employee_id: e.target.value});
                      if (editFieldErrors.employee_id) setEditFieldErrors({...editFieldErrors, employee_id: null});
                    }}
                    placeholder="e.g. EMP-2026-088"
                    style={{ borderColor: editFieldErrors.employee_id ? '#ef4444' : undefined }}
                    required
                  />
                  {editFieldErrors.employee_id && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editFieldErrors.employee_id}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-user-dept">Department <span className="req-star">*</span></label>
                  <input 
                    id="edit-user-dept"
                    type="text" 
                    value={editFormData.department} 
                    onChange={(e) => {
                      setEditFormData({...editFormData, department: e.target.value});
                      if (editFieldErrors.department) setEditFieldErrors({...editFieldErrors, department: null});
                    }}
                    placeholder="e.g. Computer Science & Engineering"
                    style={{ borderColor: editFieldErrors.department ? '#ef4444' : undefined }}
                    required
                  />
                  {editFieldErrors.department && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editFieldErrors.department}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-user-phone">Phone Number <span className="req-star">*</span></label>
                  <input 
                    id="edit-user-phone"
                    type="text" 
                    value={editFormData.phone} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setEditFormData({...editFormData, phone: val});
                      if (editFieldErrors.phone) setEditFieldErrors({...editFieldErrors, phone: null});
                    }}
                    maxLength={10}
                    placeholder="e.g. 9876543210"
                    style={{ borderColor: editFieldErrors.phone ? '#ef4444' : undefined }}
                    required
                  />
                  {editFieldErrors.phone && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editFieldErrors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-user-status">Status</label>
                  <select 
                    id="edit-user-status"
                    className="modal-select"
                    value={editFormData.status} 
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="edit-user-password">New Password (Optional)</label>
                  <div className="password-input-wrapper">
                    <Lock size={16} className="input-icon" />
                    <input 
                      id="edit-user-password"
                      type="password" 
                      value={editFormData.password} 
                      onChange={(e) => {
                        setEditFormData({...editFormData, password: e.target.value});
                        if (editFieldErrors.password) setEditFieldErrors({...editFieldErrors, password: null});
                      }}
                      placeholder="Leave blank to keep current password"
                      style={{ borderColor: editFieldErrors.password ? '#ef4444' : undefined }}
                    />
                  </div>
                  {editFieldErrors.password && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editFieldErrors.password}</span>}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={isSaving}>
                  {isSaving ? (
                    <span className="btn-saving-text"><span className="spinner-small"></span> Saving Changes...</span>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal 
        {...confirmModal} 
        onCancel={closeConfirmModal} 
      />
    </div>
  );
}

export default CustomRoleManagementPage;
